import { ArrowRightIcon } from 'lucide-react';

import { useMemo } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';
import { Card } from '@splitfiesta/ui';

import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { LinkButton } from '@/components/ui/LinkButton';

interface BudgetStats {
  budget: number | null;
  budgetCurrency: string | null;
  totalExpenses: number;
  remainingBudget: number | null;
  percentageUsed: number;
  isOverBudget: boolean;
  expensesByCurrency?: { [currency: string]: number };
  hasMixedCurrencies?: boolean;
  note?: string | null;
}

interface BudgetWidgetProps {
  group: GroupWithDashboard;
}

const getBudgetColorClasses = (percentage: number) => {
  if (percentage > 100) {
    return {
      icon: 'text-red-600 dark:text-red-400',
      text: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      progress: 'bg-red-600 dark:bg-red-400',
      progressBg: 'bg-red-100 dark:bg-red-900/40',
    };
  }
  if (percentage > 90) {
    return {
      icon: 'text-orange-600 dark:text-orange-400',
      text: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      progress: 'bg-orange-600 dark:bg-orange-400',
      progressBg: 'bg-orange-100 dark:bg-orange-900/40',
    };
  }
  if (percentage > 75) {
    return {
      icon: 'text-yellow-600 dark:text-yellow-400',
      text: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      progress: 'bg-yellow-600 dark:bg-yellow-400',
      progressBg: 'bg-yellow-100 dark:bg-yellow-900/40',
    };
  }
  if (percentage > 50) {
    return {
      icon: 'text-blue-600 dark:text-blue-400',
      text: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      progress: 'bg-blue-600 dark:bg-blue-400',
      progressBg: 'bg-blue-100 dark:bg-blue-900/40',
    };
  }
  return {
    icon: 'text-green-600 dark:text-green-400',
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    progress: 'bg-green-600 dark:bg-green-400',
    progressBg: 'bg-green-100 dark:bg-green-900/40',
  };
};

export function BudgetWidget({ group }: BudgetWidgetProps) {
  const { t } = useTranslation();
  const isAdmin = group.userRole === 'admin' || group.userRole === 'owner';

  const budgetStats = useMemo<BudgetStats | null>(() => {
    if (group?.dashboard?.budgetStats) {
      const stats = group.dashboard.budgetStats;
      return {
        budget: stats.budgetAmount || null,
        budgetCurrency: group.currency || 'USD',
        totalExpenses: stats.spent || 0,
        remainingBudget: stats.remaining || null,
        percentageUsed: stats.percentageUsed || 0,
        isOverBudget: (stats.percentageUsed || 0) > 100,
        expensesByCurrency: {
          [group.currency || 'USD']: stats.spent || 0,
        },
        hasMixedCurrencies: false,
      };
    }

    if (group?.budgetAmount && group.budgetAmount > 0) {
      const totalSpent = group.dashboard?.totalSpent || 0;
      const remaining = group.budgetAmount - totalSpent;
      const percentageUsed = (totalSpent / group.budgetAmount) * 100;

      return {
        budget: group.budgetAmount,
        budgetCurrency: group.currency || 'USD',
        totalExpenses: totalSpent,
        remainingBudget: remaining,
        percentageUsed: percentageUsed,
        isOverBudget: percentageUsed > 100,
        expensesByCurrency: {
          [group.currency || 'USD']: totalSpent,
        },
        hasMixedCurrencies: false,
      };
    }

    return null;
  }, [group]);

  // If no budget is set and user is not admin, don't show the card at all
  if (!budgetStats && !isAdmin) {
    return null;
  }

  // If no budget is set but user is admin, show the "not set" state with add button
  if (!budgetStats) {
    return (
      <Card data-testid="budget-card" className="relative">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('groups:budget.title')}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {t('groups:budget.notSet')}
            </div>
          </div>
          <LinkButton href={`/groups/${group.id}/edit`}>
            {t('groups:budget.addBudget')}
            <ArrowRightIcon className="w-4 h-4 ml-1" />
          </LinkButton>
        </div>
      </Card>
    );
  }

  return (
    <Card data-testid="budget-widget">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t('groups:budget.title')}
          </div>
          <div className="text-right">
            <div
              className={`text-xl font-bold ${getBudgetColorClasses(budgetStats.percentageUsed).text}`}
            >
              {Math.round(budgetStats.percentageUsed)}%
            </div>
            {budgetStats.isOverBudget && (
              <span
                className={`text-xs font-medium ${getBudgetColorClasses(budgetStats.percentageUsed).text}`}
              >
                {t('groups:budget.overBudget')}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('groups:budget.spent')}
            </span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('groups:budget.limit')}
            </span>
          </div>
          <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="absolute inset-0 flex">
              <div className="w-1/4 border-r border-gray-300 dark:border-gray-600 opacity-30"></div>
              <div className="w-1/4 border-r border-gray-300 dark:border-gray-600 opacity-30"></div>
              <div className="w-1/4 border-r border-gray-300 dark:border-gray-600 opacity-30"></div>
              <div className="w-1/4"></div>
            </div>
            <div
              className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${
                getBudgetColorClasses(budgetStats.percentageUsed).progress
              } ${budgetStats.percentageUsed > 100 ? 'animate-pulse' : ''}`}
              style={{
                width: `${Math.min(budgetStats.percentageUsed, 100)}%`,
              }}
            >
              <div className="h-full bg-linear-to-r from-transparent to-white/20"></div>
            </div>
            <div
              className={`absolute top-1/2 -translate-y-1/2 flex items-center justify-center text-xs font-bold px-2 ${
                budgetStats.percentageUsed > 50
                  ? 'text-white'
                  : 'text-gray-700 dark:text-white'
              }`}
              style={{
                left: budgetStats.percentageUsed > 50 ? '8px' : 'auto',
                right: budgetStats.percentageUsed <= 50 ? '8px' : 'auto',
              }}
            >
              <CurrencyDisplay
                amount={budgetStats.totalExpenses}
                currency={group.currency || 'USD'}
              />
            </div>
          </div>
          <div className="flex justify-between mt-1 px-1">
            <span className="text-xs text-gray-400">0%</span>
            <span className="text-xs text-gray-400">25%</span>
            <span className="text-xs text-gray-400">50%</span>
            <span className="text-xs text-gray-400">75%</span>
            <span className="text-xs text-gray-400">100%</span>
          </div>
        </div>

        {/* Budget stats - always stacked for better support of large currency amounts (e.g., COP) */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('groups:budget.spent')}
            </span>
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">
              <CurrencyDisplay
                amount={budgetStats.totalExpenses}
                currency={group.currency || 'USD'}
              />
            </span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('groups:budget.limit')}
            </span>
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">
              <CurrencyDisplay
                amount={budgetStats.budget || 0}
                currency={group.currency || 'USD'}
              />
            </span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {budgetStats.isOverBudget
                ? t('groups:budget.overBy', { amount: '' }).replace(' ', '')
                : t('groups:budget.remaining')}
            </span>
            <span
              className={`text-base font-bold ${
                budgetStats.isOverBudget
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-green-600 dark:text-green-400'
              }`}
            >
              <CurrencyDisplay
                amount={Math.abs(budgetStats.remainingBudget || 0)}
                currency={group.currency || 'USD'}
              />
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
